<template>
  <div>
    <Head title="Local Purchase Order" />
    <h1 class="mb-8 text-3xl font-bold">Local Purchase Orders</h1>
    <div class="flex items-center justify-between mb-6 mt-6">
      <search-filter v-model="search.search" class="mr-4 w-full max-w-md" @reset="reset">
        <label class="block text-gray-700">Local Purchase Order:</label>
      </search-filter>
      <Link class="btn-orange" :href="`/purchaseOrders`">
        <span>Add Purchase Order</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Supplier Name</th>
          <th class="pb-3 pt-3 px-3">Account</th>
          <th class="pb-3 pt-3 px-3">PO Number</th>
          <th class="pb-3 pt-3 px-3">VAT</th>
          <th class="pb-3 pt-3 px-3">Planned Date</th>
          <th class="pb-3 pt-3 px-3">Status</th>
          <th class="pb-3 pt-3 px-3" colspan="3"></th>
        </tr>
        <tr v-for="purchaseOrder in purchaseOrders.data" :key="purchaseOrder.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/purchaseOrders/${purchaseOrder.id}`" tabindex="-1">
              {{ purchaseOrder.name }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/purchaseOrders/${purchaseOrder.id}`" tabindex="-1">
              {{ purchaseOrder.account }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/purchaseOrders/${purchaseOrder.id}`" tabindex="-1">
              {{ purchaseOrder.PO_number }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/purchaseOrders/${purchaseOrder.id}`" tabindex="-1">
              {{ purchaseOrder.has_vat }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/purchaseOrders/${purchaseOrder.id}`" tabindex="-1">
              {{ purchaseOrder.planned_date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/purchaseOrders/${purchaseOrder.id}`" tabindex="-1">
              {{ purchaseOrder.status }}
            </Link>
          </td>
          <td class="border-t">
            <Link v-show="purchaseOrder.status === 'Pending Approval' || purchaseOrder.status ==='Planned'" class="flex items-center px-3" :href="`/purchaseOrders/${purchaseOrder.id}/edit`" tabindex="-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </Link>
          </td>
          <td class="border-t">
            <svg
              v-show="purchaseOrder.status === 'Pending Approval' || purchaseOrder.status ==='Planned'"
              @click="openDeleteModal(purchaseOrder, 'delete')"
              xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;" title="Delete">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </td>
          <td class="border-t">
            <svg v-show="purchaseOrder.status === 'Pending Approval' || purchaseOrder.status ==='Planned' || purchaseOrder.status ==='Booked'
            || purchaseOrder.status ==='Placed' || purchaseOrder.status ==='In Transit' || purchaseOrder.status ==='In Clearance'"
              @click="openDeleteModal(purchaseOrder, 'cancel')" 
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3" :href="`/purchaseOrders/${purchaseOrder.id}`" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="purchaseOrders.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="5">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="purchaseOrders.links" />
  </div>
  <div class="fixed z-10 inset-0 overflow-y-auto" v-if="isOpen">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <p>
                You are about to {{ action }} a purchase order <strong>{{ PO_number }}</strong>. Do you wish to continue?
              </p>
            </div>
          </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button type="button" @click="destroy()" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red sm:text-sm sm:leading-5">
              Yes
            </button>
          </span>
          <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button @click="closeModal" type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5">
              Cancel
            </button>
          </span>
        </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Link,
    Icon,
    SearchFilter,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    purchaseOrders: Object,
  },
  data() {
    return {
      isOpen: false,
      search: {
        search: this.filters.search,
      },
      PO_number: null,
      id: null,
      action: null,
    }
  },
  watch: {
    search: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/list/purchase-orders', pickBy(this.search), {
          preserveState: true,
          preserveScroll: true,
          replace: true
        })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.search = mapValues(this.search, () => null)
    },
    openDeleteModal(data, status) {
      this.PO_number = data.PO_number;
      this.id = data.id;
      this.action = status == 'cancel' ? 'cancel' : 'delete';
      this.isOpen = true;
    },
    closeModal() {
      this.PO_number = null;
      this.isOpen = false;
    },
    destroy() {
      const data = {};
      data.id = this.id;
      data.action = this.action;
      data._method = 'DELETE';
      this.$inertia.post(`/purchaseOrders/${data.id}`, data)
      this.PO_number = null;
      this.id = null;
      this.closeModal();
    },
  },
}
</script>
